import React, { useState, useEffect } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import Button from '../ui/Button';
import { usePageMeta } from '../../lib/usePageMeta';
import { Loader2, Mail, Lock, ArrowRight, Gift, ArrowLeft, CheckCircle } from 'lucide-react';

const API_URL = import.meta.env.VITE_API_URL || '/api';

type AuthMode = 'login' | 'register' | 'reset' | 'reset-confirm' | 'reset-password';

const AuthPage: React.FC = () => {
    const navigate = useNavigate();
    usePageMeta({ title: 'Sign In', description: 'Sign in or create a ProGeoData account to access the licensed professional database API.', path: '/auth' });
    const search = useSearch({ from: '/auth' }) as { mode?: string; redirect?: string; token?: string };
    const [mode, setMode] = useState<AuthMode>(() => {
        if (search.mode === 'register') return 'register';
        if (search.mode === 'reset' && search.token) return 'reset-password';
        return 'login';
    });
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [resetToken] = useState(search.token || '');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [referralCode, setReferralCode] = useState<string | null>(null);

    useEffect(() => {
        const storedRefCode = localStorage.getItem('progeodata_referral_code');
        if (storedRefCode) {
            setReferralCode(storedRefCode);
            if (mode === 'login') setMode('register');
        }
    }, []);

    const isLogin = mode === 'login';
    const isRegister = mode === 'register';

    const handleAuthSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            const endpoint = isLogin ? '/auth/login' : '/auth/register';
            const bodyData: any = { email, password };
            if (!isLogin && referralCode) bodyData.referralCode = referralCode;

            const res = await fetch(`${API_URL}${endpoint}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(bodyData),
            });
            const data = await res.json() as any;
            if (!res.ok) throw new Error(data.error || 'Authentication failed');

            localStorage.setItem('progeodata_token', data.access_token);
            if (data.user?.api_key) localStorage.setItem('progeodata_api_key', data.user.api_key);
            localStorage.setItem('progeodata_user', JSON.stringify(data.user));
            if (!isLogin) localStorage.removeItem('progeodata_referral_code');

            const redirectTo = search.redirect || '/dashboard';
            navigate({ to: redirectTo });
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleForgotSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        try {
            const res = await fetch(`${API_URL}/auth/forgot-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email }),
            });
            const data = await res.json() as any;
            if (!res.ok) throw new Error(data.error || 'Failed to send reset link');
            setMode('reset-confirm');
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleResetSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (password !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const res = await fetch(`${API_URL}/auth/reset-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ token: resetToken, newPassword: password }),
            });
            const data = await res.json() as any;
            if (!res.ok) throw new Error(data.error || 'Failed to reset password');
            setSuccess('Password reset! You can now log in.');
            setPassword('');
            setConfirmPassword('');
            setTimeout(() => {
                setMode('login');
                setSuccess(null);
            }, 3000);
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const getTitle = () => {
        switch (mode) {
            case 'login': return 'Welcome Back';
            case 'register': return 'Create Account';
            case 'reset': return 'Reset Password';
            case 'reset-confirm': return 'Check Your Email';
            case 'reset-password': return 'Set New Password';
        }
    };

    const getSubtitle = () => {
        switch (mode) {
            case 'login': return 'Sign in to access your credits and search leads';
            case 'register': return 'Register to get started with ProGeoData';
            case 'reset': return 'Enter your email to receive a reset link';
            case 'reset-confirm': return "We've sent a password reset link to your email";
            case 'reset-password': return 'Enter your new password below';
        }
    };

    return (
        <div className="min-h-screen bg-slate-900 flex items-center justify-center px-4">
            <div className="max-w-md w-full">
                <div className="text-center mb-8">
                    <h1 className="text-3xl font-bold text-white mb-2">{getTitle()}</h1>
                    <p className="text-slate-400">{getSubtitle()}</p>
                </div>

                {isRegister && referralCode && (
                    <div className="mb-6 p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-xl flex items-center gap-3">
                        <Gift className="w-6 h-6 text-emerald-400 flex-shrink-0" />
                        <div>
                            <p className="text-emerald-400 font-bold text-sm">Referral Bonus Active</p>
                            <p className="text-slate-300 text-xs">You'll get 50 bonus lookups when you create your account!</p>
                        </div>
                    </div>
                )}

                <div className="bg-slate-800 border border-slate-700 rounded-2xl p-8">
                    {error && (
                        <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
                            {error}
                        </div>
                    )}

                    {success && (
                        <div className="mb-6 p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-lg text-emerald-400 text-sm flex items-center gap-2">
                            <CheckCircle className="w-5 h-5 flex-shrink-0" />
                            {success}
                        </div>
                    )}

                    {(isLogin || isRegister) && (
                        <form onSubmit={handleAuthSubmit} className="space-y-6">
                            <div>
                                <label className="block text-sm font-medium text-slate-300 mb-2">Email</label>
                                <div className="relative">
                                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                                    <input
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className="w-full pl-10 pr-4 py-3 bg-slate-900 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                                        placeholder="you@company.com"
                                        required
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-slate-300 mb-2">Password</label>
                                <div className="relative">
                                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                                    <input
                                        type="password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        className="w-full pl-10 pr-4 py-3 bg-slate-900 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                                        placeholder={isLogin ? 'Enter password' : 'Create password (min 8 chars)'}
                                        minLength={8}
                                        required
                                    />
                                </div>
                            </div>

                            {isLogin && (
                                <div className="text-right">
                                    <button
                                        type="button"
                                        onClick={() => { setMode('reset'); setError(null); }}
                                        className="text-sky-400 hover:text-sky-300 text-sm"
                                    >
                                        Forgot password?
                                    </button>
                                </div>
                            )}

                            <Button type="submit" variant="primary" className="w-full" disabled={loading}>
                                {loading ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        {isLogin ? 'Signing in...' : 'Creating account...'}
                                    </>
                                ) : (
                                    <>
                                        {isLogin ? 'Sign In' : (referralCode ? 'Create Account & Claim Bonus' : 'Create Account')}
                                        <ArrowRight className="w-4 h-4 ml-2" />
                                    </>
                                )}
                            </Button>
                        </form>
                    )}

                    {mode === 'reset' && (
                        <form onSubmit={handleForgotSubmit} className="space-y-6">
                            <div>
                                <label className="block text-sm font-medium text-slate-300 mb-2">Email</label>
                                <div className="relative">
                                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                                    <input
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className="w-full pl-10 pr-4 py-3 bg-slate-900 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                                        placeholder="you@company.com"
                                        required
                                    />
                                </div>
                            </div>

                            <Button type="submit" variant="primary" className="w-full" disabled={loading}>
                                {loading ? (
                                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending reset link...</>
                                ) : (
                                    <>Send Reset Link <ArrowRight className="w-4 h-4 ml-2" /></>
                                )}
                            </Button>
                        </form>
                    )}

                    {mode === 'reset-confirm' && (
                        <div className="text-center py-4">
                            <Mail className="w-12 h-12 text-sky-500 mx-auto mb-4" />
                            <p className="text-slate-300 mb-2">If an account exists with <span className="text-white font-medium">{email}</span>, you'll receive a reset link shortly.</p>
                            <p className="text-slate-500 text-sm">Didn't get it? Check your spam folder.</p>
                        </div>
                    )}

                    {mode === 'reset-password' && (
                        <form onSubmit={handleResetSubmit} className="space-y-6">
                            <div>
                                <label className="block text-sm font-medium text-slate-300 mb-2">New Password</label>
                                <div className="relative">
                                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                                    <input
                                        type="password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        className="w-full pl-10 pr-4 py-3 bg-slate-900 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                                        placeholder="New password (min 8 chars)"
                                        minLength={8}
                                        required
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-slate-300 mb-2">Confirm Password</label>
                                <div className="relative">
                                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                                    <input
                                        type="password"
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        className="w-full pl-10 pr-4 py-3 bg-slate-900 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                                        placeholder="Confirm new password"
                                        minLength={8}
                                        required
                                    />
                                </div>
                            </div>

                            <Button type="submit" variant="primary" className="w-full" disabled={loading}>
                                {loading ? (
                                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Resetting password...</>
                                ) : (
                                    <>Reset Password <ArrowRight className="w-4 h-4 ml-2" /></>
                                )}
                            </Button>
                        </form>
                    )}

                    {isRegister && (
                        <div className="mt-4 p-3 bg-slate-900 rounded-lg text-center">
                            <p className="text-slate-400 text-xs">
                                Free plan: <span className="text-white font-bold">25 lookups/month</span> + <span className="text-white font-bold">5 CSV exports</span>
                                {referralCode && <> + <span className="text-emerald-400 font-bold">50 bonus lookups</span></>}
                            </p>
                            <p className="text-slate-500 text-xs mt-1">No credit card required</p>
                        </div>
                    )}

                    <div className="mt-6 text-center">
                        {(isLogin || isRegister) && (
                            <button
                                onClick={() => { setMode(isLogin ? 'register' : 'login'); setError(null); setSuccess(null); }}
                                className="text-sky-400 hover:text-sky-300 text-sm"
                            >
                                {isLogin ? "Don't have an account? Register Free" : 'Already have an account? Sign in'}
                            </button>
                        )}
                        {(mode === 'reset' || mode === 'reset-confirm' || mode === 'reset-password') && (
                            <button
                                onClick={() => { setMode('login'); setError(null); setSuccess(null); }}
                                className="text-sky-400 hover:text-sky-300 text-sm inline-flex items-center gap-1"
                            >
                                <ArrowLeft className="w-4 h-4" /> Back to login
                            </button>
                        )}
                    </div>
                </div>

                <p className="text-center text-slate-500 text-sm mt-6">
                    By continuing, you agree to our{' '}
                    <a href="/terms" className="text-sky-400 hover:underline">Terms</a>
                    {' '}and{' '}
                    <a href="/privacy" className="text-sky-400 hover:underline">Privacy Policy</a>
                </p>
            </div>
        </div>
    );
};

export default AuthPage;
